/**
 * Image compression utility for all uploads
 */

/**
 * Compress an image file before upload
 * @param {File} file - The image file to compress
 * @param {number} maxWidth - Maximum width in pixels (default: 1920)
 * @param {number} quality - Compression quality 0-1 (default: 0.8)
 * @returns {Promise<Blob>} Compressed image blob
 */
export const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(resolve, file.type, quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Compress image with different settings for different use cases
 * @param {File} file - The image file to compress
 * @param {string} type - Type of upload: 'profile', 'gallery', 'event', 'club'
 * @returns {Promise<Blob>} Compressed image blob
 */
export const compressImageForUpload = (file, type = 'event') => {
  let maxWidth = 1920;
  let quality = 0.8;
  
  // Different compression settings for different upload types
  switch (type) {
    case 'profile':
      maxWidth = 800;  // Profile pictures can be smaller
      quality = 0.85;  // Higher quality for profile pics
      break;
    case 'gallery':
      maxWidth = 1600; // Gallery images can be medium size
      quality = 0.8;
      break;
    case 'event':
      maxWidth = 1920; // Event images can be larger
      quality = 0.8;
      break;
    case 'club':
      maxWidth = 1920; // Club images can be larger
      quality = 0.8;
      break;
    default:
      maxWidth = 1920;
      quality = 0.8;
  }
  
  return compressImage(file, maxWidth, quality);
};
